import { OpenInNew } from '@mui/icons-material';
import { Box, Container, CssBaseline, Grid, ThemeProvider, Typography } from '@mui/material';
import React, { useState, useRef } from 'react';
import { FormattedMessage, IntlProvider, useIntl } from 'react-intl';
import Hero from './components/Hero';
import ProjectCard from './components/ProjectCard';
import ProjectModal from './components/ProjectModal';
import { getLocale, getMessages } from './i18n';
import { projects } from './projectsData';
import theme from './theme';

function AppContent() {
  const intl = useIntl();
  const [selectedProject, setSelectedProject] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const projectsRef = useRef(null);

  const handleScrollDown = () => {
    projectsRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleCardClick = (project) => {
    setSelectedProject(project);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedProject(null);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
        <Hero onScrollDown={handleScrollDown} />

        <Box
          ref={projectsRef}
          sx={{
            py: { xs: 8, md: 12 },
            bgcolor: 'background.default',
          }}
        >
          <Container maxWidth="lg">
            {/* Responsive Grid Container for Cards */}
            <Grid
              container
              component="section"
              aria-label={intl.formatMessage({
                id: 'portfolio.ariaLabel',
                defaultMessage: 'Project portfolio',
              })}
              spacing={{ xs: 3, sm: 4, md: 4 }}
              sx={{
                mb: 6,
              }}
            >
              {projects.map((project, index) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  key={project.id}
                  sx={{
                    animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`,
                    '@keyframes fadeInUp': {
                      '0%': {
                        opacity: 0,
                        transform: 'translateY(20px)',
                      },
                      '100%': {
                        opacity: 1,
                        transform: 'translateY(0)',
                      },
                    },
                  }}
                >
                  <ProjectCard project={project} onClick={handleCardClick} />
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        <ProjectModal open={modalOpen} project={selectedProject} onClose={handleCloseModal} />

        {/* Made by AiCrafted Badge - Fixed Position */}
        <Box
          id="aicrafted-badge"
          component="a"
          href="https://aicrafted.ca"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            position: 'fixed !important',
            bottom: { xs: '16px', sm: '20px' },
            right: { xs: '16px', sm: '20px' },
            zIndex: 9999,
            display: 'flex !important',
            alignItems: 'center !important',
            gap: '8px',
            px: '12px',
            py: '6px',
            bgcolor: '#fff !important',
            borderRadius: '8px !important',
            boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08) !important',
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important",
            fontSize: '12px !important',
            color: '#000 !important',
            textDecoration: 'none',
            fontWeight: 500,
            pointerEvents: 'auto !important',
            transition: 'all 0.2s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12) !important',
            },
          }}
        >
          <Box component="span">Made by AiCrafted ❤️</Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

function App() {
  const locale = getLocale();
  const messages = getMessages(locale);

  // Debug: verify messages are loaded
  if (process.env.NODE_ENV === 'development') {
    console.log('App - Messages loaded:', !!messages);
    console.log('App - Has projects:', !!messages?.projects);
    console.log('App - Has projects.prosperity:', !!messages?.projects?.prosperity);
    console.log(
      'App - Has projects.prosperity.introduction:',
      !!messages?.projects?.prosperity?.introduction
    );
    console.log(
      'App - Sample prosperity introduction:',
      messages?.projects?.prosperity?.introduction?.substring(0, 50)
    );
    console.log('App - Messages keys sample:', Object.keys(messages || {}).slice(0, 10));
  }

  return (
    <IntlProvider
      locale={locale}
      messages={messages}
      defaultLocale="en"
      onError={(err) => {
        if (err.code === 'MISSING_TRANSLATION') {
          // Log missing translations in development for debugging
          if (process.env.NODE_ENV === 'development') {
            console.warn('Missing translation:', err.message);
          }
          return;
        }
        throw err;
      }}
    >
      <AppContent />
    </IntlProvider>
  );
}

export default App;
